// 클라이언트 유휴 감지 — 30분 무동작 시 자동 로그아웃.
// 사용자 이벤트(마우스·키보드·터치·스크롤·탭 복귀)로 마지막 활동 시각 갱신.
// setInterval로 주기적으로 경과 시간 체크 → 30분 초과 시:
//   1) authApi.logout() 호출 (실패 무시)
//   2) React Query 캐시 clear → 화면에 노출된 복호화 데이터 즉시 제거
//   3) sessionStorage에 reason=idle 기록 → 로그인 페이지가 배너 표시
//   4) window.location.replace('/lock') — hard reload로 React 상태 전부 초기화
//      (SPA navigate 대신 hard reload를 사용하는 이유: SPA navigate는 React 컴포넌트
//       트리를 그대로 유지하므로 React Query 캐시·observer가 stale state에 stuck될 수 있음.
//       hard reload는 전체 컨텍스트를 초기화해 무한 스피너 재발을 방지함.)

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { setLogoutReason } from '../utils/logoutReason';

const IDLE_MS = 30 * 60 * 1000;      // 서버 SESSION_IDLE_TIMEOUT_MS와 동일 (기본 30분)
const CHECK_INTERVAL_MS = 30_000;     // 30초마다 경과 체크

export function useIdleLogout(): void {
  const qc = useQueryClient();
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const events: (keyof WindowEventMap)[] = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    for (const e of events) window.addEventListener(e, onActivity, { passive: true });
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onActivity();
    };
    document.addEventListener('visibilitychange', onVisibility);

    let firing = false;
    const timer = window.setInterval(async () => {
      if (firing) return;
      if (Date.now() - lastActivityRef.current < IDLE_MS) return;
      firing = true;
      window.clearInterval(timer);
      try {
        await authApi.logout();
      } catch {
        /* 이미 만료됐을 수 있음 — 무시 */
      }
      qc.clear();
      setLogoutReason('idle');
      // hard reload — React Query 캐시·observer 전부 초기화해 무한 스피너 방지
      window.location.replace('/lock');
    }, CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
      for (const e of events) window.removeEventListener(e, onActivity);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [qc]);
}
